import { dateFromKey, dateKeyOf, eventSpanDays, instantDateKeyOf, instantTimeOf } from "./schedule";
import type { Anniversary, EventException, RecurrenceFreq, ScheduleEvent } from "./types";

/**
 * RFC 5545 の TEXT エスケープ。行の折り返し(fold)はしない
 * — 75オクテット境界での分割はマルチバイトの日本語文字を途中で割ってしまう事故が
 * 起きやすく、主要カレンダー(Google/Apple/Outlook)は未折り返しの長い行も問題なく
 * 読めるため、正確さより安全側に倒す。
 */
export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n");
}

function addDaysToDateKey(key: string, days: number): string {
  const date = dateFromKey(key);
  return dateKeyOf(new Date(date.getFullYear(), date.getMonth(), date.getDate() + days));
}

function compactDate(key: string): string {
  return key.replace(/-/g, "");
}

function icsUtcStamp(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
}

/** JST(固定 +09:00)の日付+時刻(`HH:MM:SS`)を UTC の `YYYYMMDDTHHMMSSZ` にする */
function jstUtcStamp(dateKey: string, timeKey: string): string {
  return icsUtcStamp(new Date(`${dateKey}T${timeKey}+09:00`));
}

const FREQ_OF: Record<RecurrenceFreq, string> = {
  daily: "DAILY",
  weekly: "WEEKLY",
  monthly: "MONTHLY",
  yearly: "YEARLY",
};

function dateLinesOf(event: ScheduleEvent): { dtstart: string; dtend: string | null } {
  if (event.allDay) {
    const startKey = instantDateKeyOf(event.startsAt);
    const span = eventSpanDays(event);
    return {
      dtstart: `DTSTART;VALUE=DATE:${compactDate(startKey)}`,
      dtend: `DTEND;VALUE=DATE:${compactDate(addDaysToDateKey(startKey, span + 1))}`,
    };
  }
  return {
    dtstart: `DTSTART:${icsUtcStamp(new Date(event.startsAt))}`,
    dtend: event.endsAt !== null ? `DTEND:${icsUtcStamp(new Date(event.endsAt))}` : null,
  };
}

/**
 * `recurrenceUntil` は「その日を含む」という既存の意味論(→ schedule.ts の
 * `expandEventOccurrences`)に合わせ、時刻ありイベントでは UTC 23:59:59 として
 * 少し余裕を持たせる(JSTのその日を跨いで早く切ってしまわないため)。
 */
function rruleOf(event: ScheduleEvent): string | null {
  if (event.recurrenceFreq === null) return null;
  const parts = [`FREQ=${FREQ_OF[event.recurrenceFreq]}`];
  if (event.recurrenceInterval > 1) parts.push(`INTERVAL=${String(event.recurrenceInterval)}`);
  if (event.recurrenceUntil !== null) {
    const until = event.allDay
      ? compactDate(event.recurrenceUntil)
      : jstUtcStamp(event.recurrenceUntil, "23:59:59");
    parts.push(`UNTIL=${until}`);
  }
  return `RRULE:${parts.join(";")}`;
}

/**
 * キャンセルされた回(→ `event_exceptions` で `overrideEventId` が null)を EXDATE にする。
 * 差し替え(override)の回は `events` に独立した単発予定として既に含まれているため、
 * そのまま別 VEVENT になり特別扱いは不要。
 */
function exdateLinesOf(event: ScheduleEvent, exceptions: readonly EventException[]): string[] {
  const cancelledDates = exceptions
    .filter((exception) => exception.eventId === event.id && exception.overrideEventId === null)
    .map((exception) => exception.occurrenceDate);
  if (cancelledDates.length === 0) return [];
  if (event.allDay) {
    return [`EXDATE;VALUE=DATE:${cancelledDates.map(compactDate).join(",")}`];
  }
  const timeKey = instantTimeOf(event.startsAt);
  return [`EXDATE:${cancelledDates.map((date) => jstUtcStamp(date, `${timeKey}:00`)).join(",")}`];
}

function eventVEventLines(
  event: ScheduleEvent,
  exceptions: readonly EventException[],
  categoryNameById: ReadonlyMap<string, string>,
  dtstamp: string,
): string[] {
  const { dtstart, dtend } = dateLinesOf(event);
  const rrule = rruleOf(event);
  const categoryName =
    event.categoryId !== null ? categoryNameById.get(event.categoryId) : undefined;
  return [
    "BEGIN:VEVENT",
    `UID:${event.id}@couples-app.invalid`,
    `DTSTAMP:${dtstamp}`,
    dtstart,
    ...(dtend !== null ? [dtend] : []),
    ...(rrule !== null ? [rrule] : []),
    ...(event.recurrenceFreq !== null ? exdateLinesOf(event, exceptions) : []),
    `SUMMARY:${escapeIcsText(event.title)}`,
    ...(event.memo !== null && event.memo.trim() !== ""
      ? [`DESCRIPTION:${escapeIcsText(event.memo)}`]
      : []),
    ...(categoryName !== undefined ? [`CATEGORIES:${escapeIcsText(categoryName)}`] : []),
    "END:VEVENT",
  ];
}

/** 記念日・誕生日は常に終日・毎年の繰り返しとして書き出す */
function anniversaryVEventLines(anniversary: Anniversary, dtstamp: string): string[] {
  const startKey = anniversary.baseDate;
  const endKey = addDaysToDateKey(startKey, 1);
  return [
    "BEGIN:VEVENT",
    `UID:${anniversary.id}@couples-app.invalid`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;VALUE=DATE:${compactDate(startKey)}`,
    `DTEND;VALUE=DATE:${compactDate(endKey)}`,
    "RRULE:FREQ=YEARLY;INTERVAL=1",
    `SUMMARY:${escapeIcsText(anniversary.title)}`,
    ...(anniversary.memo !== null && anniversary.memo.trim() !== ""
      ? [`DESCRIPTION:${escapeIcsText(anniversary.memo)}`]
      : []),
    "END:VEVENT",
  ];
}

/**
 * 予定・記念日(誕生日から合成したものを含む)を1つの ICS(iCalendar, RFC 5545)
 * カレンダーにまとめる。Google カレンダー等の「インポート」機能で読み込める形式。
 */
export function buildIcsCalendar(
  events: readonly ScheduleEvent[],
  exceptions: readonly EventException[],
  anniversaries: readonly Anniversary[],
  categoryNameById: ReadonlyMap<string, string> = new Map(),
): string {
  const dtstamp = icsUtcStamp(new Date());
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//couples app//calendar export//JA",
    "CALSCALE:GREGORIAN",
  ];
  for (const event of events) {
    lines.push(...eventVEventLines(event, exceptions, categoryNameById, dtstamp));
  }
  for (const anniversary of anniversaries) {
    lines.push(...anniversaryVEventLines(anniversary, dtstamp));
  }
  lines.push("END:VCALENDAR");
  return lines.join("\r\n") + "\r\n";
}
